import { createContext, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

import { isDemoMode } from 'config/demo-mode';
import { useAuth } from 'features/auth';
import { demoEvents, demoTelemetry } from 'demo/mock-data';
import { shouldCreateRealtimeSocket } from './realtime-mode';

import type { RealtimeConnectedPayload, RealtimeEventPayload, RealtimeStatus, RealtimeTelemetryPayload } from '../types/realtime';

interface RealtimeContextValue {
  status: RealtimeStatus;
  socketId: string | null;
  connectedAt: string | null;
  lastTelemetry: RealtimeTelemetryPayload | null;
  lastEvent: RealtimeEventPayload | null;
  error: string | null;
}

export const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

interface RealtimeProviderProps {
  children: ReactNode;
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<RealtimeStatus>('idle');
  const [socketId, setSocketId] = useState<string | null>(null);
  const [connectedAt, setConnectedAt] = useState<string | null>(null);
  const [lastTelemetry, setLastTelemetry] = useState<RealtimeTelemetryPayload | null>(null);
  const [lastEvent, setLastEvent] = useState<RealtimeEventPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setStatus('idle');
      setSocketId(null);
      setConnectedAt(null);
      setError(null);
      return;
    }

    if (isDemoMode) {
      setStatus('connected');
      setSocketId('DEMO');
      setConnectedAt(new Date().toISOString());
      setLastTelemetry(demoTelemetry[demoTelemetry.length - 1] ?? null);
      setLastEvent(demoEvents.find((event) => event.type === 'warn') ?? demoEvents[0] ?? null);
      setError(null);
      return;
    }

    if (!shouldCreateRealtimeSocket(isAuthenticated, isLoading)) return;

    const socket = io(`${apiBaseUrl()}/realtime`, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;
    setStatus('connecting');

    socket.on('connect', () => {
      setStatus('connected');
      setSocketId(socket.id ?? null);
      setError(null);
    });

    socket.on('disconnect', () => {
      setStatus('disconnected');
      setSocketId(null);
    });

    socket.on('connect_error', (connectError) => {
      setStatus('error');
      setError(connectError.message || 'Realtime connection failed');
    });

    socket.on('realtime.connected.v1', (payload: RealtimeConnectedPayload) => {
      setSocketId(payload.socketId);
      setConnectedAt(payload.connectedAt);
    });

    socket.on('telemetry.position.v1', (payload: RealtimeTelemetryPayload) => {
      setLastTelemetry(payload);
    });

    socket.on('event.created.v1', (payload: RealtimeEventPayload) => {
      setLastEvent(payload);
    });

    return () => {
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [isAuthenticated, isLoading]);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      status,
      socketId,
      connectedAt,
      lastTelemetry,
      lastEvent,
      error
    }),
    [status, socketId, connectedAt, lastTelemetry, lastEvent, error]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

function apiBaseUrl() {
  return (import.meta.env.VITE_SOCKET_URL || import.meta.env.VITE_API_URL || 'http://localhost:4001').replace(/\/$/, '');
}
